import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Notification from '../../utils/notification';
import styles from './Auth.module.css';

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  const redirectUrl = searchParams.get('redirect') || '/';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 从 URL 获取邀请码，或从 localStorage 获取
  useEffect(() => {
    const inviteCodeFromUrl = searchParams.get('invite');
    const inviteCodeFromStorage = localStorage.getItem('inviteCode');

    if (inviteCodeFromUrl) {
      setFormData(prev => ({
        ...prev,
        inviteCode: inviteCodeFromUrl
      }));
    } else if (inviteCodeFromStorage) {
      setFormData(prev => ({
        ...prev,
        inviteCode: inviteCodeFromStorage
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('请填写所有必填字段');
      return false;
    }

    if (formData.password.length < 6) {
      setError('密码长度至少6位');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('请输入有效的邮箱地址');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authAPI.register(
        formData.email,
        formData.password,
        formData.confirmPassword,
        formData.inviteCode || undefined
      );
      // 注册成功，清除localStorage中的邀请码
      localStorage.removeItem('inviteCode');
      Notification.success('注册成功，即将跳转到登录页');
      // 跳转到登录页，带上redirect参数，这样登录后会回到指定页面
      setTimeout(() => navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`), 1500);
    } catch (err) {
      // Error notification is already shown by API interceptor
      console.error('Registration failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authPage}>
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <div className={styles.authHeader}>
            <h1>注册</h1>
            <p>创建万盘搜账号，享受更多功能</p>
            {formData.inviteCode && (
              <div className={styles.inviteCodeNotice}>
                🎉 您正在使用邀请码注册，注册成功后邀请人将获得额外奖励！
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className={styles.authForm}>
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="email">邮箱地址</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="请输入邮箱地址"
                className={styles.formInput}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password">密码</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="请输入密码（至少6位）"
                className={styles.formInput}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">确认密码</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="请再次输入密码"
                className={styles.formInput}
                required
              />
            </div>

            <button
              type="submit"
              className={styles.authSubmit}
              disabled={loading}
            >
              {loading ? '注册中...' : '注册'}
            </button>
          </form>

          <div className={styles.authFooter}>
            <p>
              已有账号？
              <Link to="/login" className={styles.authLink}>
                立即登录
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;